import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import './QuillEditor.css';
import {
  createTemplate,
  updateTemplate,
  getTemplateTypes,
  ContentTemplate
} from '../../../services/contentTemplates';

interface ContentTemplateEditorProps {
  template?: ContentTemplate;
  editMode: 'content' | 'values' | 'preview';
  franchiseId?: number;
  onSave: () => void;
  onCancel: () => void;
}

const ContentTemplateEditor = ({
  template,
  editMode,
  franchiseId,
  onSave,
  onCancel
}: ContentTemplateEditorProps) => {
  const permissions = useSelector((state: any) => state.auth.permissions?.['content-templates']);
  
  const [templateTypes, setTemplateTypes] = useState<Array<{ id: number; name: string; slug: string; status: string }>>([]);
  const [formData, setFormData] = useState<ContentTemplate>({
    type: template?.consent_template_type_id?.toString() || template?.type || '',
    title: template?.title || '',
    content: template?.content || '',
    version: template?.version || '1.0',
    is_active: template?.is_active ?? true
  });

  const [parsedContent, setParsedContent] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const [isHtmlContent, setIsHtmlContent] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [detectedPlaceholders, setDetectedPlaceholders] = useState<string[]>([]);
  const [validationErrors, setValidationErrors] = useState<Set<string>>(new Set());

  // Fetch template types on mount
  useEffect(() => {
    const fetchTypes = async () => {
      try {
        const response = await getTemplateTypes();
        if (response?.success && response?.collections) {
          setTemplateTypes(response.collections);
        }
      } catch (error) {
        console.error('Error fetching template types:', error);
      }
    };
    fetchTypes();
  }, []);

  // Update formData when template prop changes
  useEffect(() => {
    if (template) {
      setFormData({
        type: template.consent_template_type_id?.toString() || template.type || '',
        title: template.title || '',
        content: template.content || '',
        version: template.version || '1.0',
        is_active: template.is_active ?? true
      });
    }
  }, [template]);

  // Quill editor modules configuration
  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'indent': '-1'}, { 'indent': '+1' }],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'align': [] }],
      ['link'],
      ['clean']
    ]
  };

  const quillFormats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'list', 'bullet', 'indent',
    'color', 'background',
    'align',
    'link'
  ];

  useEffect(() => {
    if (formData.content) {
      console.log('Parsing content:', formData.content);
      try {
        // Try to parse as JSON
        const parsed = JSON.parse(formData.content);
        console.log('Parsed as JSON:', parsed);
        setParsedContent(parsed);
        setIsHtmlContent(false);
        
        // Detect placeholders in JSON content
        if (parsed.htmlContent) {
          detectPlaceholders(parsed.htmlContent);
        }
      } catch (error) {
        // If not JSON, treat as HTML content
        console.log('Treating as HTML content');
        const htmlContent = formData.content;
        
        // Detect placeholders in HTML content
        detectPlaceholders(htmlContent);
        
        const extractedContent: any = { htmlContent };
        
        setParsedContent(extractedContent);
        setIsHtmlContent(true);
      }
    }
  }, [formData.content]);

  // Function to detect placeholders in square brackets [placeholder-name]
  const detectPlaceholders = (content: string) => {
    const placeholderRegex = /\[([a-zA-Z0-9_-]+)\]/g;
    const placeholders = new Set<string>();
    let match;
    
    while ((match = placeholderRegex.exec(content)) !== null) {
      placeholders.add(match[1]);
    }
    
    setDetectedPlaceholders(Array.from(placeholders));
  };

  // Function to add ordinal suffix to numbers (1st, 2nd, 3rd, etc.)
  const getOrdinalSuffix = (num: number): string => {
    const j = num % 10;
    const k = num % 100;
    
    if (j === 1 && k !== 11) {
      return num + '<sup>st</sup>';
    }
    if (j === 2 && k !== 12) {
      return num + '<sup>nd</sup>';
    }
    if (j === 3 && k !== 13) {
      return num + '<sup>rd</sup>';
    }
    return num + '<sup>th</sup>';
  };

  const handleInputChange = (field: keyof ContentTemplate, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleContentChange = (key: string, value: any) => {
    const updated = { ...parsedContent, [key]: value };
    setParsedContent(updated);
    
    // Clear validation error for this field
    if (validationErrors.has(key)) {
      const newErrors = new Set(validationErrors);
      newErrors.delete(key);
      setValidationErrors(newErrors);
    }
    
    // If it's HTML content (not JSON), store directly and detect placeholders
    if (isHtmlContent && key === 'htmlContent') {
      detectPlaceholders(value);
      setFormData(prev => ({
        ...prev,
        content: value
      }));
    } else {
      // Don't replace placeholders in the stored content
      // Keep the original placeholders intact for editing
      // Replacement will only happen during preview/save
      
      // Store as JSON with values separate from content
      setFormData(prev => ({
        ...prev,
        content: JSON.stringify(updated)
      }));
    }
  };

  const handleSave = async () => {
    // Clear previous validation errors
    setValidationErrors(new Set());
    
    // Validation
    if (!formData.title.trim()) {
      toast.error('Title is required');
      return;
    }

    if (!formData.type) {
      toast.error('Type is required');
      return;
    }

    // Validate that all detected placeholders have values (only if placeholders exist)
    if (detectedPlaceholders.length > 0) {
      const missingValues = detectedPlaceholders.filter(
        placeholder => !parsedContent[placeholder] || parsedContent[placeholder].toString().trim() === ''
      );
      
      if (missingValues.length > 0) {
        // Set validation errors for visual feedback only
        setValidationErrors(new Set(missingValues));
        return;
      }
    }

    // Validate content - if not HTML, must be valid JSON
    if (!isHtmlContent && formData.content) {
      try {
        JSON.parse(formData.content);
      } catch (e) {
        toast.error('Content must be valid JSON or HTML');
        return;
      }
    }

    // Validate that content is not empty
    if (!parsedContent.htmlContent || parsedContent.htmlContent.trim() === '' || parsedContent.htmlContent === '<p><br></p>') {
      toast.error('Content cannot be empty');
      return;
    }

    // Strip HTML tags and check if there's actual text content (not just whitespace)
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = parsedContent.htmlContent;
    const textContent = tempDiv.textContent || tempDiv.innerText || '';
    
    if (!textContent.trim()) {
      toast.error('Content cannot be empty or contain only whitespace');
      return;
    }

    setLoading(true);
    try {
      // Prepare data for saving - type is already the numeric ID
      const saveData = {
        type: formData.type, // Already the numeric ID from dropdown
        title: formData.title,
        content: formData.content,
        version: formData.version,
        is_active: formData.is_active,
        ...(franchiseId && { franchise_id: franchiseId })
      };
      
      let response: any;
      if (template?.id) {
        // Update existing template
        response = await updateTemplate(template.id, saveData);
      } else {
        // Create new template
        response = await createTemplate(saveData);
      }

      if (response.success) {
        toast.success(template?.id ? 'Template updated successfully' : 'Template created successfully');
        onSave();
      } else {
        toast.error(response.message || 'Failed to save template');
      }
    } catch (error) {
      console.error('Error saving template:', error);
      toast.error('An error occurred while saving template');
    } finally {
      setLoading(false);
    }
  };

  const renderPreview = () => {
    let htmlContent = parsedContent.htmlContent || '';
    
    // Replace all detected placeholders with their values
    detectedPlaceholders.forEach(placeholder => {
      if (parsedContent[placeholder] !== undefined && parsedContent[placeholder] !== '') {
        let replacementValue = parsedContent[placeholder];
        
        // Special handling for day-of-every-month - add ordinal suffix
        if (placeholder === 'day-of-every-month' && !isNaN(replacementValue)) {
          replacementValue = getOrdinalSuffix(parseInt(replacementValue));
        }
        
        const regex = new RegExp(`\\[${placeholder}\\]`, 'g');
        htmlContent = htmlContent.replace(regex, replacementValue);
      }
    });
    
    return (
      <div className="terms-preview-container">
        <div className="card">
          <div className="card-header bg-primary text-white">
            <h3 className="mb-0">{formData.title}</h3>
            <small className="d-block mt-1">Version: {formData.version}</small>
          </div>
          <div className="card-body" style={{ padding: '30px', backgroundColor: '#fff' }}>
            <div 
              className="terms-content ql-editor"
              dangerouslySetInnerHTML={{ __html: htmlContent }}
              style={{
                lineHeight: '1.8',
                fontSize: '14px',
                color: '#333',
                padding: 0
              }}
            />
          </div>
          <div className="card-footer text-muted">
            <small>
              <i className="fas fa-info-circle mr-1"></i>
              This is how the terms will appear to users
            </small>
          </div>
        </div>
      </div>
    );
  };

  const renderContentEditor = () => {
    const canEditContent = permissions?.['edit-content'];
    const canEditValues = permissions?.['edit-values'];
    
    return (
      <div className="row">
        {/* Dynamically detected placeholders */}
        {detectedPlaceholders.length > 0 ? (
          <>
            <div className="col-12">
              <h6 className="text-muted">
                <i className="fas fa-tags mr-2"></i>
                Detected Placeholders
              </h6>
            </div>
            {detectedPlaceholders.map((placeholder) => {
              const hasError = validationErrors.has(placeholder);
              
              return (
                <div className="col-md-4" key={placeholder}>
                  <div className="form-group">
                    <label>
                      {placeholder.split('-').map(word => 
                        word.charAt(0).toUpperCase() + word.slice(1)
                      ).join(' ')}
                      <span className="text-danger ml-1">*</span>
                    </label>
                    <input
                      type="text"
                      className={`form-control ${hasError ? 'is-invalid' : ''}`}
                      value={parsedContent[placeholder] || ''}
                      onChange={(e) => handleContentChange(placeholder, e.target.value)}
                      disabled={!canEditValues}
                      placeholder={`Enter ${placeholder}`}
                    />
                    <small className="form-text text-muted">
                      Replaces: <code>[{placeholder}]</code>
                    </small>
                    {hasError && (
                      <div className="invalid-feedback d-block">
                        This field is required
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
            <div className="col-12"><hr /></div>
          </>
        ) : (
          <div className="col-12">
            {!canEditContent && canEditValues ? (
              <div className="alert alert-warning">
                <i className="fas fa-exclamation-triangle mr-2"></i>
                No placeholders found in this template. You only have permission to edit values, but there are no values to edit. Please contact an administrator to add placeholders or request content editing permission.
              </div>
            ) : (
              <div className="alert alert-info">
                <i className="fas fa-info-circle mr-2"></i>
                No placeholders detected in the content. Add placeholders using square brackets like <code>[placeholder-name]</code> to create dynamic fields.
              </div>
            )}
            <hr />
          </div>
        )}
        
        <div className="col-12">
          <div className="form-group">
            <label>
              {formData.type === 'terms_and_conditions' ? 'Terms & Conditions Content' : 'Content'}
              <span className="text-danger ml-1">*</span>
            </label>
            <ReactQuill
              theme="snow"
              value={parsedContent.htmlContent || ''}
              onChange={(value) => handleContentChange('htmlContent', value)}
              modules={quillModules}
              formats={quillFormats}
              readOnly={!canEditContent}
              style={{ height: '400px', marginBottom: '50px' }}
            />
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="terms-conditions-template-editor">
      <div className="content-header">
        <div className="container-fluid">
          <div className="row mb-2">
            <div className="col-sm-6">
              <h1>
                {editMode === 'preview' ? 'Preview' : (template?.id ? 'Edit' : 'Create')} Template
                {editMode !== 'preview' && (
                  <>
                    {permissions?.['edit-content'] && !permissions?.['edit-values'] && (
                      <span className="badge badge-info ml-2">Content Only</span>
                    )}
                    {!permissions?.['edit-content'] && permissions?.['edit-values'] && (
                      <span className="badge badge-warning ml-2">Values Only</span>
                    )}
                    {permissions?.['edit-content'] && permissions?.['edit-values'] && (
                      <span className="badge badge-success ml-2">Full Access</span>
                    )}
                  </>
                )}
              </h1>
            </div>
            <div className="col-sm-6">
              <div className="float-sm-right">
                {editMode === 'preview' ? (
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={onCancel}
                  >
                    <i className="fas fa-times mr-2"></i>
                    Close
                  </button>
                ) : (
                  <div className="btn-group">
                    <button
                      type="button"
                      className={`btn ${!showPreview ? 'btn-primary' : 'btn-outline-primary'}`}
                      onClick={() => setShowPreview(false)}
                    >
                      <i className="fas fa-edit mr-2"></i>
                      Edit
                    </button>
                    <button
                      type="button"
                      className={`btn ${showPreview ? 'btn-primary' : 'btn-outline-primary'}`}
                      onClick={() => setShowPreview(true)}
                    >
                      <i className="fas fa-eye mr-2"></i>
                      Preview
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <section className="content">
        <div className="container-fluid">
          {editMode === 'preview' ? (
            // Preview-Only Mode (from list)
            <div>
              {renderPreview()}
            </div>
          ) : showPreview ? (
            // Preview Mode
            <div>
              {renderPreview()}
              <div className="card mt-3">
                <div className="card-footer">
                  <div className="d-flex justify-content-between">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={onCancel}
                      disabled={loading}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={handleSave}
                      disabled={loading || (detectedPlaceholders.length === 0 && !permissions?.['edit-content'] && permissions?.['edit-values'])}
                      title={detectedPlaceholders.length === 0 && !permissions?.['edit-content'] && permissions?.['edit-values'] ? 'No values to edit. Contact administrator for content editing permission.' : ''}
                    >
                      {loading ? (
                        <>
                          <span className="spinner-border spinner-border-sm mr-2"></span>
                          Saving...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-save mr-2"></i>
                          Save Template
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            // Edit Mode
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">Template Details</h3>
              </div>
              <div className="card-body">
                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Type</label>
                      <select
                        className="form-control"
                        value={formData.type}
                        onChange={(e) => handleInputChange('type', e.target.value)}
                        disabled={!!template?.id || !permissions?.['edit-content']}
                      >
                        {templateTypes.length > 0 ? (
                          templateTypes.map((type) => (
                            <option key={type.id} value={type.id}>
                              {type.name}
                            </option>
                          ))
                        ) : (
                          <option value="">Loading types...</option>
                        )}
                      </select>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Version</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.version}
                        readOnly
                        disabled
                        placeholder="e.g., 1.0"
                      />
                    </div>
                  </div>
                </div>

                <div className="form-group">
                  <label>
                    Title
                    <span className="text-danger ml-1">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.title}
                    onChange={(e) => handleInputChange('title', e.target.value)}
                    disabled={!permissions?.['edit-content']}
                    placeholder="e.g., Registration Terms"
                  />
                </div>

                <hr />

                <h5 className="mb-3">
                  {editMode === 'content' ? 'Template Content' : 'Template Values'}
                </h5>

                {renderContentEditor()}
              </div>

              <div className="card-footer">
                <div className="d-flex justify-content-between">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={onCancel}
                    disabled={loading}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleSave}
                    disabled={loading || (detectedPlaceholders.length === 0 && !permissions?.['edit-content'] && permissions?.['edit-values'])}
                    title={detectedPlaceholders.length === 0 && !permissions?.['edit-content'] && permissions?.['edit-values'] ? 'No values to edit. Contact administrator for content editing permission.' : ''}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm mr-2"></span>
                        Saving...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-save mr-2"></i>
                        Save Template
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default ContentTemplateEditor;
